import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Card, CardBody, CardHeader, CardTitle } from '@ludus/ui'
import type { CardPage } from '../../types.ts'
import { formatDate } from '../../utils/format.ts'

interface HistoryPanelProps {
  auditId: string
  history: CardPage['history']
  siblings: CardPage['identity']['siblings']
}

/** A deal's frame: the status history and the calls linked to its sale, where a call shows its transcript. */
export function HistoryPanel({ auditId, history, siblings }: HistoryPanelProps) {
  const { t } = useTranslation()
  const calls = siblings.filter((s) => s.kind === 'interaction')
  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>
            {t('pages.audits.card.history.title', { defaultValue: 'Status history' })}
          </CardTitle>
        </CardHeader>
        <CardBody>
          {history.length === 0 ? (
            <p className="text-xs text-muted">
              {t('pages.audits.card.history.none', { defaultValue: 'No history in the slice.' })}
            </p>
          ) : (
            <ol className="flex flex-col gap-1 text-sm">
              {history.map((event) => (
                <li key={event.id} className="flex items-center gap-2">
                  <span className="text-xs text-muted">{formatDate(event.at)}</span>
                  <span>
                    {event.kind === 'status_changed'
                      ? `${event.from ?? '—'} → ${event.to ?? '—'}`
                      : t('pages.audits.card.history.handover', {
                          defaultValue: 'handed to {{who}}',
                          who: event.to ?? '—',
                        })}
                  </span>
                </li>
              ))}
            </ol>
          )}
        </CardBody>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>
            {t('pages.audits.card.history.calls', { defaultValue: 'Linked calls' })}
          </CardTitle>
        </CardHeader>
        <CardBody>
          {calls.length === 0 ? (
            <p className="text-xs text-muted">
              {t('pages.audits.card.history.noCalls', {
                defaultValue: 'No call is linked to this deal.',
              })}
            </p>
          ) : (
            <ul className="flex flex-col gap-1 text-sm">
              {calls.map((call) => (
                <li key={call.id} className="flex items-center gap-2">
                  <Link className="hover:underline" to={`/audits/${auditId}/cards/${call.id}`}>
                    {call.name}
                  </Link>
                  <span className="text-xs text-muted">
                    {formatDate(call.createdAt)} · {call.linkConfidence ?? '—'}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>
    </>
  )
}
